import React from "react";
import {
  FaBoxOpen,
  FaBoxes,
  FaCheckCircle,
  FaRegCreditCard,
  FaShoppingCart,
  FaStore,
  FaSyncAlt,
  FaTimesCircle,
} from "react-icons/fa";

import { formatEUR } from "../utils/boutiqueUtils";

export default function BoutiqueHero({
  loading,
  stats,
  onRefresh,
  onOpenCart,
  onGoReglements,
}) {
  const cartUnits = stats?.cartUnits || 0;
  const cartTotal = stats?.cartTotal || 0;

  return (
    <section className="relative overflow-hidden rounded-[2rem] bg-slate-950 p-5 text-white shadow-xl shadow-slate-950/10 sm:p-6">
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-sky-500/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-20 h-44 w-44 rounded-full bg-lime-500/30 blur-3xl" />

      <div className="relative">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.28em] text-sky-200">
              <FaStore className="text-[0.7rem]" />
              Boutique du club
            </p>

            <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
              Articles disponibles
            </h2>

            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-200">
              Commandez vos équipements, textiles et articles club. Après
              validation du panier, le règlement sera généré automatiquement
              dans votre espace commandes.
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold text-slate-200 backdrop-blur">
                <FaBoxOpen className="text-sky-200" />
                Articles club
              </span>

              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold text-slate-200 backdrop-blur">
                <FaShoppingCart className="text-sky-200" />
                Panier
              </span>

              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold text-slate-200 backdrop-blur">
                <FaRegCreditCard className="text-lime-200" />
                Règlement en ligne
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-col">
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-white px-4 text-sm font-bold text-slate-950 shadow-lg transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Actualiser
            </button>

            <button
              type="button"
              onClick={onOpenCart}
              className="relative inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 text-sm font-bold text-white ring-1 ring-white/10 transition hover:bg-slate-800"
            >
              <FaShoppingCart />
              Panier

              {cartUnits > 0 && (
                <span className="absolute -right-2 -top-2 inline-flex min-w-6 items-center justify-center rounded-full bg-white px-2 py-1 text-xs font-bold text-slate-950 shadow">
                  {cartUnits}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={onGoReglements}
              className="col-span-2 inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-white/10 px-4 text-sm font-bold text-white ring-1 ring-white/15 transition hover:bg-white/20"
            >
              <FaRegCreditCard />
              {cartUnits > 0 ? formatEUR(cartTotal) : "Commandes"}
            </button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-2">
          <HeroStat
            icon={FaBoxes}
            label="Articles"
            value={loading ? "…" : stats?.total || 0}
          />

          <HeroStat
            icon={FaCheckCircle}
            label="Dispos"
            value={loading ? "…" : stats?.available || 0}
          />

          <HeroStat
            icon={FaTimesCircle}
            label="Épuisés"
            value={loading ? "…" : stats?.outOfStock || 0}
          />
        </div>

        <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-slate-200">
          <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1.5 backdrop-blur">
            {loading ? "…" : stats?.filtered || 0} affiché
            {(stats?.filtered || 0) > 1 ? "s" : ""} /{" "}
            {loading ? "…" : stats?.total || 0}
          </span>

          <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1.5 backdrop-blur">
            {loading ? "…" : stats?.categories || 0} catégorie
            {(stats?.categories || 0) > 1 ? "s" : ""}
          </span>

          <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1.5 backdrop-blur">
            Panier : {formatEUR(cartTotal)}
          </span>
        </div>
      </div>
    </section>
  );
}

function HeroStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 p-3 text-center backdrop-blur">
      <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
        <Icon className="text-sm text-white" />
      </div>

      <p className="mt-2 text-lg font-bold leading-none">{value}</p>
      <p className="mt-1 text-[11px] font-medium text-slate-300">{label}</p>
    </div>
  );
}